import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode, UIEvent } from 'react';
import { Fragment, useCallback, useMemo, useRef, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobRole } from '~/shared/data-contract/job-role';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { EmptyStateMessage } from '~/ui/core/empty-state-message/EmptyStateMessage';
import { GRID_ROW_FOCUS_VISIBLE_CLASS_NAME, useTableRovingFocus } from '~/ui/core/focus/use-table-roving-focus';
import { IconSortArrow } from '~/ui/core/icons';
import type { TableColumnWidth } from '~/ui/core/table/Table';
import {
    TableBody,
    TableCell,
    TableContainer,
    TableErrorMessage,
    TableExpandableRow,
    TableFullWidthRow,
    TableGrid,
    TableHeader,
    TableHeaderCell,
    TableSkeletonRow,
} from '~/ui/core/table/Table';
import { CONFIGURE_COLUMNS_TRACK_WIDTH, TableConfigureColumnsCell } from '~/ui/core/table/TableConfigureColumns';
import { useTableColumnWidths } from '~/ui/core/table/use-table-column-widths';

import { useColumnVisibilitySetting } from '../../../app/query-hooks/use-column-visibility-setting';
import { useUrlTiles } from '../../../url-tiles';
import {
    type JobProfileManagementV2Profile,
    type JobProfileManagementV2Sort,
    useJobProfileManagementV2Roles,
} from '../../api/use-job-profile-management-roles';
import { DraftBadge } from '../../components/DraftBadge';
import { InviteTeamMemberForJobItem } from '../../components/InviteTeamMemberForJobItem';
import { hasJobProfileStatusBadge, JobProfileStatusBadge } from '../../components/JobProfileStatusBadge';
import { PublishedBadge } from '../../components/PublishedBadge';
import { ReviewedBadge } from '../../components/ReviewedBadge';
import { GenericAssignReviewerBadge } from '../../components/ReviewerComponents';
import { isJsaProfileDetailTilePayload } from '../../url-tile-payloads';
import { createJsaProfileDetailTilePayload, JSA_PRIMARY_DETAIL_TILE_SLOT_KEY } from '../../url-tiles';
import type { JobProfilesReviewerAssignmentFilter, JobProfilesTypeFilter } from '../use-job-profiles-filters';

type ColumnId = 'name' | 'nextStep' | 'reviewer' | 'status' | 'type';

type ColumnDescriptor = {
    readonly cellClassName?: string;
    readonly id: ColumnId;
    readonly labelKey: TranslationKey;
    readonly width: TableColumnWidth;
};

const NAME_COLUMN_ID: ColumnId = 'name';
const FORCED_VISIBLE_COLUMN_IDS: readonly ColumnId[] = [NAME_COLUMN_ID];
const COLUMNS: readonly ColumnDescriptor[] = [
    {
        cellClassName: 'min-w-0',
        id: 'name',
        labelKey: 'pages.job-architecture-v2.table-column.name',
        width: 'minmax(320px, 2fr)',
    },
    { id: 'type', labelKey: 'pages.job-architecture-v2.table-column.type', width: 'minmax(120px, 0.6fr)' },
    { id: 'status', labelKey: 'pages.job-architecture-v2.table-column.status', width: 'minmax(140px, 0.7fr)' },
    {
        cellClassName: 'min-w-0',
        id: 'reviewer',
        labelKey: 'pages.job-architecture-v2.table-column.reviewer',
        width: 'minmax(220px, 1fr)',
    },
    { id: 'nextStep', labelKey: 'pages.job-profiles.table.next-step', width: 'minmax(140px, 0.8fr)' },
];

const getColumnWidth = (columnId: string): TableColumnWidth =>
    COLUMNS.find((column) => column.id === columnId)?.width ?? 'minmax(120px, 0.6fr)';

const SKELETON_ROW_COUNT = 10;
const PAGINATION_THRESHOLD_PX = 240;
const FETCHING_ROW_KEYS = ['fetching-0', 'fetching-1', 'fetching-2'] as const;
const LOADING_ROW_KEYS = Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => `loading-${index}`);
const jobProfileManagementV2TableLabel = 'Job Profile Management V2 Table';

type JobProfileManagementV2TableProps = {
    readonly architectureId: string | undefined;
    readonly reviewerAssignment?: JobProfilesReviewerAssignmentFilter;
    readonly reviewerId?: string;
    readonly searchTerm: string;
    readonly type: readonly JobProfilesTypeFilter[];
};

type SortColumnId = 'name';

type SortState = {
    readonly columnId: SortColumnId;
    readonly direction: 'asc' | 'desc';
};

type JobProfileManagementDetailProfile = {
    readonly item: JobLevel | JobRole;
    readonly profile: JobProfileManagementV2Profile;
};

const SORT_COLUMNS: Record<SortColumnId, { readonly apiField: JobProfileManagementV2Sort['field'] }> = {
    name: { apiField: 'title' },
};

const getClusterId = ({ item, profile }: JobProfileManagementDetailProfile): string | undefined => {
    if (item.type === 'job-role' && is.nonEmptyString(item.parentId)) {
        return item.parentId;
    }

    const clusterId = profile.path['cluster'];

    return is.nonEmptyString(clusterId) ? clusterId : undefined;
};

const getDetailProfile = (profile: JobProfileManagementV2Profile): JobProfileManagementDetailProfile | undefined => {
    if (profile.detail?.type === 'ROLE') {
        return { item: profile.detail.role, profile };
    }

    if (profile.detail?.type === 'LEVEL') {
        return { item: profile.detail.level, profile };
    }

    return undefined;
};

const isDetailProfile = (
    detailProfile: JobProfileManagementDetailProfile | undefined,
): detailProfile is JobProfileManagementDetailProfile => !is.undefined(detailProfile);

const NameCell: FunctionComponent<{
    readonly item: JobLevel | JobRole;
}> = ({ item }) => {
    return (
        <span
            className="min-w-0 truncate text-text-muted in-data-[is-active=true]:!text-text-on-accent"
            title={item.title}
        >
            {item.title}
        </span>
    );
};

const TypeCell: FunctionComponent<{ readonly item: JobLevel | JobRole }> = ({ item }) => {
    const translate = useTranslate();

    return item.type === 'job-role'
        ? translate('pages.job-architecture-v2.job-role')
        : translate('pages.job-architecture-v2.job-level');
};

const ReviewerCell: FunctionComponent<{
    readonly architectureId: string;
    readonly detailProfile: JobProfileManagementDetailProfile;
}> = ({ architectureId, detailProfile }) => {
    const { item } = detailProfile;

    if (is.undefined(item.reviewer)) {
        return '-';
    }

    return (
        <InviteTeamMemberForJobItem architectureId={architectureId} itemId={item.id} itemType={item.type}>
            {({ openInviteDialog }) => (
                <GenericAssignReviewerBadge
                    chromeVariant="gray"
                    item={item}
                    itemType={item.type}
                    jobArchitectureId={architectureId}
                    jobClusterId={getClusterId(detailProfile)}
                    onlyAssignReviewer={true}
                    onInvite={openInviteDialog}
                />
            )}
        </InviteTeamMemberForJobItem>
    );
};

const StatusCell: FunctionComponent<{ readonly item: JobLevel | JobRole }> = ({ item }) => {
    if (item.versionInfo?.isPublished === true) {
        return <PublishedBadge />;
    }

    if (item.versionInfo?.isReviewed === true) {
        return <ReviewedBadge />;
    }

    return <DraftBadge />;
};

const NextStepCell: FunctionComponent<{
    readonly architectureId: string;
    readonly detailProfile: JobProfileManagementDetailProfile;
}> = ({ architectureId, detailProfile }) => {
    const { item } = detailProfile;

    if (!hasJobProfileStatusBadge(item)) {
        return '-';
    }

    return (
        <JobProfileStatusBadge
            item={item}
            itemType={item.type}
            jobArchitectureId={architectureId}
            jobClusterId={getClusterId(detailProfile)}
        />
    );
};

const ProfileCellContent: FunctionComponent<{
    readonly architectureId: string;
    readonly columnId: ColumnId;
    readonly detailProfile: JobProfileManagementDetailProfile;
}> = ({ architectureId, columnId, detailProfile }) => {
    switch (columnId) {
        case 'name':
            return <NameCell item={detailProfile.item} />;
        case 'type':
            return <TypeCell item={detailProfile.item} />;
        case 'status':
            return <StatusCell item={detailProfile.item} />;
        case 'reviewer':
            return <ReviewerCell architectureId={architectureId} detailProfile={detailProfile} />;
        case 'nextStep':
            return <NextStepCell architectureId={architectureId} detailProfile={detailProfile} />;
    }
};

const SortableHeaderCell: FunctionComponent<{
    readonly children: ReactNode;
    readonly columnId: SortColumnId;
    readonly sorting: SortState;
    readonly onSortingChange: (sorting: SortState) => void;
}> = ({ children, columnId, onSortingChange, sorting }) => {
    const isActive = sorting.columnId === columnId;
    const nextDirection = isActive && sorting.direction === 'asc' ? 'desc' : 'asc';

    return (
        <TableHeaderCell
            aria-sort={isActive ? (sorting.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
            role="columnheader"
        >
            <button
                className="flex cursor-pointer appearance-none items-center gap-xs border-0 bg-transparent p-0 text-left font-sans text-body-s font-medium text-text-muted"
                type="button"
                onClick={() => onSortingChange({ columnId, direction: nextDirection })}
            >
                {children}
                {isActive && (
                    <IconSortArrow
                        size={14}
                        style={{ transform: sorting.direction === 'desc' ? 'rotate(180deg)' : undefined }}
                    />
                )}
            </button>
        </TableHeaderCell>
    );
};

export const JobProfileManagementV2Table: FunctionComponent<JobProfileManagementV2TableProps> = ({
    architectureId,
    reviewerAssignment,
    reviewerId,
    searchTerm,
    type,
}) => {
    const translate = useTranslate();
    const { tiles, updateTile } = useUrlTiles();
    const { columnVisibility, onColumnVisibilityChange } = useColumnVisibilitySetting(
        'jobProfileManagementColumnVisibility',
        { forcedVisible: FORCED_VISIBLE_COLUMN_IDS },
    );
    const visibleColumns = useMemo(
        () => COLUMNS.filter((column) => columnVisibility[column.id] !== false),
        [columnVisibility],
    );
    const hideableColumns = useMemo(
        () =>
            COLUMNS.filter((column) => column.id !== NAME_COLUMN_ID).map((column) => ({
                id: column.id,
                label: translate(column.labelKey),
            })),
        [translate],
    );
    const { gridColumnWidths, visibleColumnWidths } = useTableColumnWidths({
        columnIds: visibleColumns.map((column) => column.id),
        getColumnWidth,
        trailingTrackWidth: CONFIGURE_COLUMNS_TRACK_WIDTH,
    });
    const [sorting, setSorting] = useState<SortState>({
        columnId: 'name',
        direction: 'asc',
    });
    const apiSort = useMemo(
        (): JobProfileManagementV2Sort => ({
            direction: sorting.direction,
            field: SORT_COLUMNS[sorting.columnId].apiField,
        }),
        [sorting],
    );
    const { error, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, roles } = useJobProfileManagementV2Roles({
        architectureId,
        reviewerAssignment,
        reviewerId,
        searchTerm,
        sort: apiSort,
        type,
    });
    const detailProfiles = useMemo(() => roles.map(getDetailProfile).filter(isDetailProfile), [roles]);
    const primaryDetailTilePayload = tiles.find((tile) => tile.slotKey === JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)?.payload;
    const hasPrimaryDetailTile = !is.undefined(primaryDetailTilePayload);
    const activeProfileId = isJsaProfileDetailTilePayload(primaryDetailTilePayload)
        ? primaryDetailTilePayload.profileId
        : undefined;

    const handleScroll = useCallback(
        (event: UIEvent<HTMLDivElement>): void => {
            const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
            const isNearEnd = scrollHeight - scrollTop - clientHeight < PAGINATION_THRESHOLD_PX;

            if (isNearEnd && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        },
        [fetchNextPage, hasNextPage, isFetchingNextPage],
    );

    const handleOpenRole = useCallback(
        ({ item }: JobProfileManagementDetailProfile): void => {
            if (!is.nonEmptyString(architectureId)) {
                return;
            }

            updateTile(
                createJsaProfileDetailTilePayload({
                    architectureId,
                    isArchived: item.isArchived,
                    itemType: item.type,
                    mode: 'view',
                    profileId: item.id,
                    version: item.versionInfo?.version,
                }),
            );
        },
        [architectureId, updateTile],
    );

    const handleSelectRole = useCallback(
        (detailProfile: JobProfileManagementDetailProfile): void => {
            if (hasPrimaryDetailTile) {
                handleOpenRole(detailProfile);
            }
        },
        [handleOpenRole, hasPrimaryDetailTile],
    );

    // Adapter rows for the roving focus hook: the table renders plain mapped
    // rows (no TanStack), so each row only carries its id and payload.
    const navigableRows = useMemo(
        () => detailProfiles.map((detailProfile) => ({ detailProfile, id: detailProfile.item.id })),
        [detailProfiles],
    );
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const {
        getRegionProps,
        getRowProps,
        onKeyDown: handleGridKeyDown,
    } = useTableRovingFocus({
        onOpen: ({ detailProfile }) => handleOpenRole(detailProfile),
        regionKey: 'job-profile-management-table',
        rows: navigableRows,
        scrollContainerRef,
    });

    if (error !== null) {
        return (
            <TableContainer className="mr-m">
                <TableErrorMessage>
                    {translate('pages.job-architecture-v2.skills.error-prefix')}: {error.message}
                </TableErrorMessage>
            </TableContainer>
        );
    }

    return (
        <TableContainer className="mr-m">
            <TableGrid
                aria-label={jobProfileManagementV2TableLabel}
                className="grid-rows-[auto_1fr] h-full"
                role="grid"
                onKeyDown={handleGridKeyDown}
            >
                <TableHeader $columnWidths={gridColumnWidths} role="row">
                    {visibleColumns.map((column) =>
                        column.id === NAME_COLUMN_ID ? (
                            <SortableHeaderCell
                                key={column.id}
                                columnId="name"
                                sorting={sorting}
                                onSortingChange={setSorting}
                            >
                                {translate(column.labelKey)}
                            </SortableHeaderCell>
                        ) : (
                            <TableHeaderCell key={column.id} role="columnheader">
                                {translate(column.labelKey)}
                            </TableHeaderCell>
                        ),
                    )}
                    <TableConfigureColumnsCell
                        columns={hideableColumns}
                        columnVisibility={columnVisibility}
                        triggerAriaLabel={translate('components.table.configure-columns')}
                        onColumnVisibilityChange={onColumnVisibilityChange}
                    />
                </TableHeader>
                <TableBody
                    ref={scrollContainerRef}
                    className="content-start"
                    data-testid="job-profile-management-v2-table-body"
                    {...getRegionProps()}
                    onScroll={handleScroll}
                >
                    {isLoading ? (
                        LOADING_ROW_KEYS.map((rowKey) => (
                            <TableSkeletonRow
                                key={rowKey}
                                columnCount={visibleColumnWidths.length}
                                columnWidths={gridColumnWidths}
                            />
                        ))
                    ) : (
                        <Fragment>
                            {detailProfiles.length === 0 && (
                                <TableFullWidthRow
                                    $autoHeight={true}
                                    $columnWidths={gridColumnWidths}
                                    cellClassName="h-full !min-h-0 !items-stretch !px-0 !py-0"
                                    className="!mt-0 h-full"
                                >
                                    <EmptyStateMessage placement="one-third">
                                        {translate('pages.job-profiles.no-matching')}
                                    </EmptyStateMessage>
                                </TableFullWidthRow>
                            )}
                            {detailProfiles.map((detailProfile, index) => (
                                <div key={detailProfile.item.id} className="bg-surface-canvas py-[3px]">
                                    <TableExpandableRow
                                        {...getRowProps(index)}
                                        $columnWidths={gridColumnWidths}
                                        $isSelected={detailProfile.item.id === activeProfileId}
                                        $level={0}
                                        className={`mt-0 ${GRID_ROW_FOCUS_VISIBLE_CLASS_NAME}`}
                                        role="row"
                                        onClick={() => handleSelectRole(detailProfile)}
                                        onDoubleClick={() => handleOpenRole(detailProfile)}
                                    >
                                        {visibleColumns.map((column) => (
                                            <TableCell key={column.id} className={column.cellClassName} role="gridcell">
                                                <ProfileCellContent
                                                    architectureId={architectureId ?? ''}
                                                    columnId={column.id}
                                                    detailProfile={detailProfile}
                                                />
                                            </TableCell>
                                        ))}
                                    </TableExpandableRow>
                                </div>
                            ))}
                            {isFetchingNextPage &&
                                FETCHING_ROW_KEYS.map((rowKey) => (
                                    <TableSkeletonRow
                                        key={rowKey}
                                        columnCount={visibleColumnWidths.length}
                                        columnWidths={gridColumnWidths}
                                    />
                                ))}
                        </Fragment>
                    )}
                </TableBody>
            </TableGrid>
        </TableContainer>
    );
};
